import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Loader2, Plus, FileText, BellRing, ListChecks } from "lucide-react";
import PlanRegularisationDialog from "@/components/PlanRegularisationDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import VeilleReglementaire from "@/components/VeilleReglementaire";
import { telechargerDossierInspection } from "@/lib/dossierInspection";

const STATUTS = {
  a_jour: { label: "À jour", cls: "bg-emerald-100 text-emerald-800" },
  a_renouveler: { label: "À renouveler", cls: "bg-amber-100 text-amber-800" },
  expire: { label: "Expiré", cls: "bg-red-100 text-red-800" },
  manquant: { label: "Manquant", cls: "bg-red-600 text-white" },
  non_applicable: { label: "Non applicable", cls: "bg-slate-100 text-slate-600" },
};
const FILTRES = ["all", "manquant", "expire", "a_renouveler", "a_jour", "non_applicable"];

function Score({ data }) {
  const color = data.score >= 80 ? "text-emerald-700" : data.score >= 50 ? "text-amber-700" : "text-red-700";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-wrap items-center gap-6" data-testid="conformite-score">
      <div><div className="text-xs uppercase tracking-wider text-slate-500">Score de conformité</div>
        <div className={`text-4xl font-semibold ${color}`} data-testid="conformite-score-value">{data.score} %</div></div>
      {Object.entries(STATUTS).map(([k, v]) => (
        <div key={k} className="text-sm" data-testid={`conformite-count-${k}`}>
          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${v.cls}`}>{v.label}</span>
          <span className="ml-2 font-semibold">{data.counts[k] || 0}</span>
        </div>
      ))}
    </div>
  );
}

function ItemRow({ it, canEdit, onEdit }) {
  const s = STATUTS[it.statut];
  return (
    <div className="px-5 py-3 flex items-start gap-4 border-b border-slate-100 last:border-0" data-testid={`conformite-item-${it.code}`}>
      <span className={`mt-0.5 shrink-0 px-2 py-0.5 rounded text-[11px] font-semibold ${s.cls}`} data-testid={`conformite-statut-${it.code}`}>{s.label}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900">{it.libelle}</div>
        <div className="text-xs text-slate-500">{it.base_legale}{it.indicateur && <> · {it.indicateur}</>}{it.echeance && <> · échéance {it.echeance}</>}</div>
        {it.document && <div className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1"><FileText className="h-3 w-3" />{it.document.titre || it.document.original_filename}</div>}
        {it.note && <div className="text-xs text-slate-600 mt-0.5 italic">{it.note}</div>}
      </div>
      {canEdit && <Button size="sm" variant="outline" onClick={() => onEdit(it)} data-testid={`conformite-edit-${it.code}`}>Mettre à jour</Button>}
    </div>
  );
}

function EditDialog({ item, docs, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { if (item) setF({ document_id: item.document?.id || "", date_document: item.date_document || "", note: item.note || "", non_applicable: !!item.non_applicable }); }, [item]);
  const save = async () => {
    const body = { note: f.note, non_applicable: f.non_applicable };
    if (f.date_document) body.date_document = f.date_document;
    if (f.document_id) body.document_id = f.document_id; else if (item.document) body.retirer_document = true;
    try { await api.patch(`/conformite/${item.id}`, body); toast.success("Registre mis à jour"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="conformite-dialog">
        <DialogHeader><DialogTitle>{item?.libelle}</DialogTitle><DialogDescription>{item?.base_legale}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-sm font-medium">Pièce justificative (GED)</label>
            <Select value={f.document_id || "none"} onValueChange={(v) => setF({ ...f, document_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="conformite-doc-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune</SelectItem>
                {docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.titre || d.original_filename} ({d.categorie})</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><label className="text-sm font-medium">Date du document</label>
            <Input type="date" value={f.date_document} onChange={(e) => setF({ ...f, date_document: e.target.value })} data-testid="conformite-date" /></div>
          <div><label className="text-sm font-medium">Note</label>
            <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="conformite-note" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.non_applicable} onChange={(e) => setF({ ...f, non_applicable: e.target.checked })} data-testid="conformite-na" />Non applicable à notre GE</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} data-testid="conformite-save" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddDialog({ open, onClose, onSaved }) {
  const [f, setF] = useState({ libelle: "", base_legale: "", categorie: "Autre", periodicite_mois: "" });
  const save = async () => {
    try {
      await api.post("/conformite", { ...f, periodicite_mois: f.periodicite_mois ? Number(f.periodicite_mois) : null });
      toast.success("Élément ajouté"); setF({ libelle: "", base_legale: "", categorie: "Autre", periodicite_mois: "" }); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="conformite-add-dialog">
        <DialogHeader><DialogTitle>Ajouter un document au registre</DialogTitle><DialogDescription>Élément spécifique à votre GE ou demandé par votre conseil.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Libellé" value={f.libelle} onChange={(e) => setF({ ...f, libelle: e.target.value })} data-testid="conformite-add-libelle" />
          <Input placeholder="Base légale (facultatif)" value={f.base_legale} onChange={(e) => setF({ ...f, base_legale: e.target.value })} data-testid="conformite-add-base" />
          <Input placeholder="Catégorie" value={f.categorie} onChange={(e) => setF({ ...f, categorie: e.target.value })} data-testid="conformite-add-categorie" />
          <Input type="number" min={1} placeholder="Renouvellement (mois, facultatif)" value={f.periodicite_mois} onChange={(e) => setF({ ...f, periodicite_mois: e.target.value })} data-testid="conformite-add-periodicite" />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={f.libelle.trim().length < 3} data-testid="conformite-add-save" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Ajouter</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Conformite() {
  const { user } = useAuth();
  const canEdit = ["admin", "gestionnaire_ge"].includes(user?.role);
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [filtre, setFiltre] = useState("all");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/conformite"); setData(d); }
    catch (e) {
      if (e.response?.status === 403) { setForbidden(true); return; }
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canEdit) api.get("/documents").then((r) => setDocs(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => {}); }, [canEdit]);

  const groupes = useMemo(() => {
    const g = {};
    (data?.items || []).filter((i) => filtre === "all" || i.statut === filtre).forEach((i) => { (g[i.categorie] ||= []).push(i); });
    return g;
  }, [data, filtre]);

  const telecharger = async () => {
    setZipping(true);
    try {
      await telechargerDossierInspection();
      toast.success("Dossier d'inspection téléchargé");
    } catch (e) { toast.error("Génération du dossier impossible"); }
    finally { setZipping(false); }
  };

  const rappels = async () => {
    setReminding(true);
    try {
      const { data: r } = await api.post("/conformite/rappels");
      if (!r.nouveaux) toast.info("Aucune nouvelle échéance à rappeler (rappels déjà envoyés ou rien d'ici 30 jours).");
      else toast.success(`${r.nouveaux} échéance(s) rappelée(s) à ${r.destinataires} personne(s) — e-mails : ${r.emails_envoyes} envoyé(s), ${r.emails_echec} en échec`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setReminding(false); }
  };

  if (forbidden) {
    return (
      <div className="max-w-xl mx-auto mt-16 text-center bg-white border border-slate-200 rounded-xl p-8" data-testid="conformite-forbidden">
        <h1 className="text-xl font-semibold text-slate-900">Accès réservé</h1>
        <p className="text-sm text-slate-600 mt-2">Le registre de conformité est réservé à l'administration, au gestionnaire GE et à l'auditeur.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="conformite-page">
      <PageHeader testId="conformite-header" title="Conformité & inspections" subtitle="Documents obligatoires du GE, état à jour et dossier prêt pour une inspection DEETS ou de l'inspection du travail."
        actions={<div className="flex gap-2">
          {canEdit && <Button variant="outline" onClick={() => setPlanOpen(true)} data-testid="conformite-plan"><ListChecks className="h-4 w-4 mr-1" />Plan de régularisation</Button>}
          {canEdit && <Button variant="outline" onClick={rappels} disabled={reminding} data-testid="conformite-rappels"><BellRing className="h-4 w-4 mr-1" />Envoyer les rappels</Button>}
          {canEdit && <Button variant="outline" onClick={() => setAdding(true)} data-testid="conformite-add"><Plus className="h-4 w-4 mr-1" />Ajouter</Button>}
          <Button onClick={telecharger} disabled={zipping} data-testid="conformite-dossier" className="bg-[#17352e] hover:bg-[#284b40] text-white">
            {zipping ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Dossier d'inspection (ZIP)</Button>
        </div>} />
      <Tabs defaultValue="registre">
        <TabsList data-testid="conformite-tabs">
          <TabsTrigger value="registre" data-testid="conformite-tab-registre">Registre</TabsTrigger>
          <TabsTrigger value="veille" data-testid="conformite-tab-veille">Veille réglementaire</TabsTrigger>
        </TabsList>
        <TabsContent value="veille" className="mt-5"><VeilleReglementaire canEdit={canEdit} /></TabsContent>
        <TabsContent value="registre" className="mt-5 space-y-6">
      {!data ? <div className="text-sm text-slate-500" data-testid="conformite-loading">Chargement…</div> : (<>
        <Score data={data} />
        <div className="flex flex-wrap gap-2">
          {FILTRES.map((k) => (
            <button key={k} type="button" onClick={() => setFiltre(k)} data-testid={`conformite-filter-${k}`}
              className={`px-3 h-8 rounded-full text-xs border transition-colors ${filtre === k ? "bg-[#17352e] text-white border-[#17352e]" : "bg-white border-slate-200 hover:border-slate-400"}`}>
              {k === "all" ? "Tous" : STATUTS[k].label}</button>
          ))}
        </div>
        {Object.entries(groupes).map(([cat, items]) => (
          <section key={cat} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <h2 className="px-5 py-3 text-sm font-semibold text-slate-700 bg-slate-50 border-b border-slate-100">{cat}</h2>
            {items.map((it) => <ItemRow key={it.id} it={it} canEdit={canEdit} onEdit={setEditing} />)}
          </section>
        ))}
        {Object.keys(groupes).length === 0 && <div className="text-sm text-slate-500" data-testid="conformite-empty">Aucun élément pour ce filtre.</div>}
      </>)}
        </TabsContent>
      </Tabs>
      <EditDialog item={editing} docs={docs} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      {canEdit && <PlanRegularisationDialog open={planOpen} defaultTerritoire={user?.territoire} onClose={() => setPlanOpen(false)} />}
      <AddDialog open={adding} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />
    </div>
  );
}
